import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { Box, Button, Grid, Input, Typography } from '@mui/material'
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined'
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined'
import SellOutlined from '@mui/icons-material/SellOutlined'
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded'
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined'
import CallMadeRounded from '@mui/icons-material/CallMadeRounded'
import PendingActionsOutlined from '@mui/icons-material/PendingActionsOutlined'
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined'
import GroupOutlined from '@mui/icons-material/GroupOutlined'
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined'
import DeliveryDiningOutlined from '@mui/icons-material/DeliveryDiningOutlined'
import DataTable from '../../components/DataTable'
import Header from '../../components/Headers/Header'
import CustomLoader from '../../components/Loader/CustomLoader'
import TableHeader from '../../components/TableHeader'
import { StatTile, raisedButtonSx } from '../../components/UI/Depth'
import { influencerSidebarIcons } from '../../components/Sidebar/sidebarIcons'
import { getInfluencerDashboard } from '../../apollo'
import { getViewingInfluencerId } from '../../utils/influencerContext'
import { customStyles } from '../../utils/tableCustomStyles'
import useGlobalStyles from '../../utils/globalStyles'
import { StatusPill, money } from './shared'

const GET_DASHBOARD = gql`
  ${getInfluencerDashboard}
`

// Same 30-days-back default the restaurant dashboard's own Graph Filter
// starts from, day-only (no time) since these feed a date input
const defaultStartDate = () => {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d.toISOString().substr(0, 10)
}
const today = () => new Date().toISOString().substr(0, 10)

function GroupLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: 'text.disabled',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        mb: 1.5
      }}>
      {children}
    </Typography>
  )
}

function InfluencerHome(props) {
  const { t } = props
  const globalClasses = useGlobalStyles()
  const influencerId = getViewingInfluencerId()
  // Edits sit in `draft` until Apply, so the stats don't refetch per keystroke
  const [range, setRange] = useState({
    startDate: defaultStartDate(),
    endDate: today()
  })
  const [draft, setDraft] = useState(range)
  const { data, loading, error } = useQuery(GET_DASHBOARD, {
    variables: { influencerId, ...range },
    fetchPolicy: 'network-only'
  })

  const dashboard = data?.influencerDashboard
  const show = value => (loading ? '...' : value)

  const couponColumns = [
    { name: t('Coupon'), selector: 'title', sortable: true },
    {
      name: t('Discount'),
      cell: row => (
        <>
          {row.discountType === 'PERCENTAGE'
            ? `${row.discount}%`
            : money(row.discount)}
        </>
      )
    },
    { name: t('Orders'), selector: 'ordersCount', sortable: true },
    { name: t('UsersUsed'), selector: 'usersUsed', sortable: true },
    {
      name: t('Status'),
      cell: row => (
        <StatusPill tone={row.enabled ? 'green' : 'slate'}>
          {row.enabled ? t('Enabled') : t('Disabled')}
        </StatusPill>
      )
    }
  ]

  const periodStats = [
    {
      label: t('PeriodEarnings'),
      value: show(money(dashboard?.periodEarnings)),
      icon: TrendingUpRounded,
      tone: 'green'
    },
    {
      label: t('PeriodOrders'),
      value: show(dashboard?.periodOrdersCount ?? 0),
      icon: ReceiptLongOutlined,
      tone: 'blue'
    }
  ]

  const earningStats = [
    {
      label: t('RatePerOrder'),
      value: show(money(dashboard?.ratePerOrder)),
      icon: SellOutlined,
      tone: 'orange'
    },
    {
      label: t('TotalEarnings'),
      value: show(money(dashboard?.totalEarnings)),
      icon: PaymentsOutlined,
      tone: 'green'
    },
    {
      label: t('WalletBalance'),
      value: show(money(dashboard?.currentWalletAmount)),
      icon: AccountBalanceWalletOutlined,
      tone: 'violet'
    },
    {
      label: t('Withdrawn'),
      value: show(money(dashboard?.withdrawnWalletAmount)),
      icon: CallMadeRounded,
      tone: 'slate'
    }
  ]

  const orderStats = [
    {
      label: t('ActiveOrders'),
      value: show(dashboard?.activeOrdersCount ?? 0),
      icon: PendingActionsOutlined,
      tone: 'amber'
    },
    {
      label: t('TotalOrders'),
      value: show(dashboard?.ordersCount ?? 0),
      icon: ShoppingBagOutlined,
      tone: 'blue'
    },
    {
      label: t('UsersUsed'),
      value: show(dashboard?.usersUsed ?? 0),
      icon: GroupOutlined,
      tone: 'violet'
    },
    {
      label: t('PickupOrders'),
      value: show(dashboard?.pickupOrders ?? 0),
      icon: StorefrontOutlined,
      tone: 'orange'
    },
    {
      label: t('DeliveryOrders'),
      value: show(dashboard?.deliveryOrders ?? 0),
      icon: DeliveryDiningOutlined,
      tone: 'rose'
    }
  ]

  const dateInputSx = { mt: 0, width: { xs: '100%', sm: 170 } }
  const rangeFilter = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1.5,
        flexWrap: 'wrap'
      }}>
      <Box sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: 'text.disabled' }}>
          {t('StartDate')}
        </Typography>
        <Input
          type="date"
          inputProps={{ max: draft.endDate }}
          value={draft.startDate}
          onChange={e => setDraft({ ...draft, startDate: e.target.value })}
          disableUnderline
          className={globalClasses.input}
          sx={dateInputSx}
        />
      </Box>
      <Box sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: 'text.disabled' }}>
          {t('EndDate')}
        </Typography>
        <Input
          type="date"
          inputProps={{ min: draft.startDate, max: today() }}
          value={draft.endDate}
          onChange={e => setDraft({ ...draft, endDate: e.target.value })}
          disableUnderline
          className={globalClasses.input}
          sx={dateInputSx}
        />
      </Box>
      <Button
        disabled={
          draft.startDate > draft.endDate ||
          (draft.startDate === range.startDate &&
            draft.endDate === range.endDate)
        }
        onClick={() => setRange(draft)}
        sx={{ ...raisedButtonSx, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
        {t('Apply')}
      </Button>
    </Box>
  )

  const tiles = (stats, lg) => (
    <Grid container spacing={2.5} mb={4}>
      {stats.map(stat => (
        <Grid item xs={12} sm={6} lg={lg} key={stat.label}>
          <StatTile {...stat} />
        </Grid>
      ))}
    </Grid>
  )

  return (
    <>
      <Header
        title={t('Home')}
        subtitle={t('SubtitleInfluencerHome')}
        icon={influencerSidebarIcons.Home}
        action={rangeFilter}
      />
      {error ? (
        <Typography sx={{ color: 'error.main', mb: 2 }}>
          {error.message}
        </Typography>
      ) : null}
      {loading && !dashboard ? (
        <CustomLoader />
      ) : (
        <>
          <GroupLabel>{`${range.startDate} → ${range.endDate}`}</GroupLabel>
          {tiles(periodStats, 3)}
          <GroupLabel>{t('Earnings')}</GroupLabel>
          {tiles(earningStats, 3)}
          <GroupLabel>{t('Orders')}</GroupLabel>
          {tiles(orderStats, 2.4)}
          <DataTable
            title={<TableHeader title={t('MyCoupons')} />}
            columns={couponColumns}
            data={dashboard?.coupons || []}
            customStyles={customStyles}
            noDataComponent={
              <Box p={3}>
                <Typography sx={{ color: 'text.disabled' }}>
                  {t('NoCouponsAssigned')}
                </Typography>
              </Box>
            }
          />
        </>
      )}
    </>
  )
}

export default withTranslation()(InfluencerHome)
